/**
 * ====================================================================
 * VTO CODE SNIPPET Oct 29 2025
 * Fittingbox Virtual Try-On Integration - Complete Implementation
 * 
 * Features:
 * - Option 2: Full Payload parsing (current frame + recommended frames)
 * - Face shape detection with automatic filtering
 * - Lens Simulator integration (tinted, mirror, photochromic lenses)
 * - Enhanced error handling (13 error states with friendly messages)
 * - Top 3 favorites comparison system
 * - Modern Fittingbox API (setFrame, startVto)
 * - Privacy-first camera consent
 * - XSS prevention via HTML escaping for all merchant-controlled data
 * 
 * Security Notes:
 * - API Key: Intentionally exposed client-side (Fittingbox VTO requirement)
 *   The API key is domain-restricted and only works on approved domains.
 *   This is the intended security model for Fittingbox VTO Advanced.
 * 
 * - HTML Escaping: All merchant-controlled values (frame names, brands, 
 *   colors, etc.) are escaped via escapeHTML() before innerHTML injection
 *   to prevent XSS attacks. Always use escapeHTML() for dynamic content.
 * 
 * - Toast Messages: showToast() should only be called with trusted/static
 *   strings, not user input or merchant data.
 * 
 * Optional: For additional hardening, merchants can configure Content 
 * Security Policy (CSP) headers. See SHOPIFY_INTEGRATION_GUIDE.md
 * ====================================================================
 */

(function() {
  'use strict';
  
  // ====================================================================
  // VTO CODE SNIPPET Oct 29 2025 - Configuration
  // ====================================================================
  let FITTINGBOX_API_KEY = null; // Loaded from theme settings via data attribute
  
  // ====================================================================
  // PLACEHOLDER LENS MATERIAL SKUs - UPDATE WITH REAL FITTINGBOX VALUES
  // Contact Fittingbox support to obtain your actual lens material SKUs
  // Location: shopify-theme/assets/virtual-try-on.js (lines 29-32)
  // ====================================================================
  const LENS_SKU_TINTED = 'PLACEHOLDER_LENS_TINTED_BROWN';
  const LENS_SKU_MIRROR = 'PLACEHOLDER_LENS_MIRROR_SILVER';
  const LENS_SKU_PHOTOCHROMIC = 'PLACEHOLDER_LENS_PHOTOCHROMIC_GRAY';
  
  // ====================================================================
  // VTO CODE SNIPPET Oct 29 2025 - State Management
  // ====================================================================
  let vtoInstance = null;
  let isVtoRunning = false;
  let isInitialized = false;
  let currentFrameData = null;
  let recommendedFrames = [];
  let detectedFaceShape = null;
  let vtoHasBeenUsed = false; // Flag to prevent re-validation after successful use
  let validationPassed = false; // Flag to prevent button from re-hiding after validation succeeds
  
  // ====================================================================
  // VTO Color Variant add-on - State Management
  // ====================================================================
  let colorVariants = [];
  let currentVariantId = null;
  
  // ====================================================================
  // VTO CODE SNIPPET Oct 29 2025 - DOM Elements
  // ====================================================================
  const modal = document.getElementById('vto-modal');
  const triggerBtn = document.getElementById('vto-trigger-btn');
  const closeBtn = document.getElementById('vto-close-btn');
  const privacyNotice = document.getElementById('vto-privacy-notice');
  const acceptPrivacyBtn = document.getElementById('vto-accept-privacy');
  const declinePrivacyBtn = document.getElementById('vto-decline-privacy');
  const modalTitle = document.getElementById('vto-modal-title');
  const vtoContainer = document.getElementById('fittingbox-vto-container');
  
  // ====================================================================
  // VTO CODE SNIPPET Oct 29 2025 - SKU Sanitization
  // Cleans frame SKUs from Excel/Sheets formatting artifacts
  // ====================================================================
  function cleanFrameSKU(sku) {
    if (!sku || typeof sku !== 'string') return '';
    
    // Remove leading single quote (Excel artifact for preserving leading zeros)
    sku = sku.replace(/^'/, '');
    
    // Trim whitespace
    sku = sku.trim();
    
    return sku;
  }

  // ====================================================================
  // VTO CODE SNIPPET Oct 29 2025 - HTML Escape
  // Prevents XSS by escaping HTML special characters
  // ====================================================================
  function escapeHTML(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  // ====================================================================
  // VTO CODE SNIPPET Oct 29 2025 - Frame Validation via Fitmix Loading
  // Tests if frame actually loads in Fitmix VTO (more reliable than metadata API)
  // ====================================================================
  function validateFrameAvailability(frameSku, apiKey, callback) {
    const cleanedSku = cleanFrameSKU(frameSku);
    
    if (!cleanedSku) {
      console.debug('Invalid frame SKU provided');
      callback(false);
      return;
    }
    
    if (!apiKey) {
      console.debug('API key not available for frame validation');
      callback(false);
      return;
    }
    
    // Skip validation if VTO has already been successfully used or validation already passed
    // This prevents conflicts from creating multiple VTO instances
    if (vtoHasBeenUsed || vtoInstance || validationPassed) {
      console.log('✓ Skipping validation - VTO already validated or in use');
      callback(true);
      return;
    }
    
    console.log('🔍 Validating frame availability for SKU:', cleanedSku);
    
    // Wait for Fittingbox API to be available
    waitForFittingbox(function() {
      // Create hidden validation container with unique ID to prevent race conditions
      const validationId = 'vto-validation-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
      const validationContainer = document.createElement('div');
      validationContainer.id = validationId;
      validationContainer.style.cssText = 'position:absolute;width:1px;height:1px;top:-9999px;left:-9999px;visibility:hidden;';
      document.body.appendChild(validationContainer);
      
      let validationComplete = false;
      let testWidget = null;
      
      // Timeout after 30 seconds (increased from 20s for reliability)
      const timeout = setTimeout(function() {
        if (!validationComplete) {
          console.warn('⚠ Frame validation timeout after 30s for SKU:', cleanedSku);
          cleanup();
          callback(false);
        }
      }, 30000);
      
      function cleanup() {
        clearTimeout(timeout);
        validationComplete = true;
        
        // Destroy test widget with enhanced cleanup
        if (testWidget) {
          try {
            testWidget.stopVto();
            console.log('✓ Validation widget stopped');
          } catch (e) {
            console.warn('⚠ Error stopping validation widget:', e);
          }
          testWidget = null; // Clear reference
        }
        
        // Remove validation container
        if (validationContainer && validationContainer.parentNode) {
          validationContainer.parentNode.removeChild(validationContainer);
          console.log('✓ Validation container removed');
        }
      }
      
      try {
        // Create minimal VTO widget for validation
        testWidget = window.FitMix.createWidget(
          validationId,
          {
            apiKey: apiKey,
            onIssue: function(issueData) {
              // Treat critical errors as validation failures
              if (issueData.frameNotFound || 
                  issueData.licenseNotFound || 
                  issueData.serverNotResponding) {
                console.warn('⚠ Frame validation failed:', cleanedSku, issueData);
                cleanup();
                callback(false);
              }
            }
          },
          function() {
            // Widget initialized, now try to load the frame
            console.log('🔧 Validation widget initialized, testing frame load...');
            try{
              // Attempt to load frame
              testWidget.setFrame(cleanedSku);
              
              // If setFrame doesn't throw and we don't get frameNotFound error,
              // assume frame is available
              setTimeout(function() {
                if (!validationComplete) {
                  console.log('✅ Frame validation PASSED for SKU:', cleanedSku);
                  validationPassed = true; // Set flag to prevent re-hiding button
                  cleanup();
                  callback(true);
                }
              }, 2000); // Wait 2 seconds for potential errors
              
            } catch (error) {
              console.debug('Error loading frame in validation:', error);
              cleanup();
              callback(false);
            }
          }
        );
      } catch (error) {
        console.debug('Error creating validation widget:', error);
        cleanup();
        callback(false);
      }
    });
  }

  // ====================================================================
  // VTO CODE SNIPPET Oct 29 2025 - Enhanced Error Messages
  // Error handling for all 13 Fittingbox VTO error states
  // ====================================================================
  function getUserFriendlyErrorMessage(issueData) {
    if (issueData.cameraAccessDenied) {
      return {
        title: 'Camera Access Required',
        message: 'Please allow camera access in your browser settings to try on frames.',
        variant: 'default',
      };
    }

    if (issueData.detectionFailed) {
      return {
        title: 'Can\'t Find Your Face',
        message: 'Make sure you\'re centered in the frame with good lighting and try again.',
        variant: 'default',
      };
    }

    if (issueData.poseInvalid) {
      return {
        title: 'Adjust Your Position',
        message: 'Look directly at the camera and center your face in the frame.',
        variant: 'default',
      };
    }

    if (issueData.frameNotFound) {
      return {
        title: 'Frame Not Available',
        message: 'This frame isn\'t ready for virtual try-on yet. Try another style!',
        variant: 'default',
      };
    }

    if (issueData.protocolFailed) {
      return {
        title: 'Detection Failed',
        message: 'Let\'s try again. Make sure you\'re in good lighting and looking at the camera.',
        variant: 'default',
      };
    }

    if (issueData.trackingLost) {
      return {
        title: 'Stay in Frame',
        message: 'We lost sight of you. Keep your face centered and well-lit.',
        variant: 'default',
      };
    }

    if (issueData.highLatency) {
      return {
        title: 'Slow Connection',
        message: 'Your internet connection is slow. The experience may be laggy.',
        variant: 'default',
      };
    }

    if (issueData.noCameraFound) {
      return {
        title: 'No Camera Detected',
        message: 'We couldn\'t find a camera. Please connect a camera and reload.',
        variant: 'destructive',
      };
    }

    if (issueData.liveIncompatibleBrowser) {
      return {
        title: 'Browser Not Supported',
        message: 'Your browser doesn\'t support live virtual try-on. Please use a modern browser like Chrome or Safari.',
        variant: 'destructive',
      };
    }

    if (issueData.liveIncompatibleOS) {
      return {
        title: 'Update Required',
        message: 'Please update your device\'s operating system to use virtual try-on.',
        variant: 'destructive',
      };
    }

    if (issueData.licenseNotFound) {
      return {
        title: 'License Error',
        message: 'Virtual try-on is temporarily unavailable. Please try again later.',
        variant: 'destructive',
      };
    }

    if (issueData.serverNotResponding) {
      return {
        title: 'Connection Issue',
        message: 'Unable to connect to the virtual try-on service. Please check your internet connection.',
        variant: 'destructive',
      };
    }

    if (issueData.removalLowPerformances) {
      return {
        title: 'Performance Limited',
        message: 'Your device may not have enough power for the best experience. Frame removal is disabled.',
        variant: 'default',
      };
    }

    return null;
  }

  // ====================================================================
  // VTO CODE SNIPPET Oct 29 2025 - Toast Notification System
  // ====================================================================
  function showToast(title, message, variant = 'default') {
    const toast = document.createElement('div');
    toast.className = 'vto-toast vto-toast-' + variant;
    toast.innerHTML = `
      <div class="vto-toast-content">
        <strong>${title}</strong>
        <p>${message}</p>
      </div>
    `;
    
    document.body.appendChild(toast);
    
    // Trigger animation
    setTimeout(() => toast.classList.add('vto-toast-show'), 10);
    
    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      toast.classList.remove('vto-toast-show');
      setTimeout(() => toast.remove(), 300);
    }, 5000);
  }

  // ====================================================================
  // VTO CODE SNIPPET Oct 29 2025 - Wait for Fittingbox API
  // ====================================================================
  function waitForFittingbox(callback) {
    if (window.FitMix) {
      callback();
    } else {
      setTimeout(() => waitForFittingbox(callback), 100);
    }
  }

  // ====================================================================
  // VTO CODE SNIPPET Oct 29 2025 - Wait for Initialization
  // Waits for VTO to be fully initialized before proceeding
  // ====================================================================
  function waitForInitialization(callback, timeout) {
    timeout = timeout || 10000; // Default 10 second timeout
    const startTime = Date.now();
    let completed = false;
    
    function checkInitialized() {
      if (completed) {
        return;
      }
      
      if (isInitialized) {
        completed = true;
        callback();
      } else if (Date.now() - startTime > timeout) {
        completed = true;
        console.debug('VTO initialization timeout');
        // showToast('Initialization Timeout', 'Virtual try-on took too long to load. Please refresh the page.', 'destructive');
      } else {
        setTimeout(checkInitialized, 100);
      }
    }
    
    checkInitialized();
  }

  // ====================================================================
  // VTO CODE SNIPPET Oct 29 2025 - Initialize Fittingbox VTO
  // Modern API usage with setFrame() and proper callbacks
  // ====================================================================
  function initializeFittingbox() {
    if (!window.FitMix) {
      console.debug('Fittingbox API not loaded');
      showToast('Error', 'Virtual try-on service not available', 'destructive');
      return;
    }
    
    const config = {
      apiKey: FITTINGBOX_API_KEY,
      onStopVto: function() {
        isVtoRunning = false;
        // PD MEASUREMENT - Hide PD button when VTO stops
        // hidePDMeasurementButton();
      },
      onMode: function(mode) {
        // Mode changed
      },
      onGetFaceshape: function(data) {
        if (data.errorCode) {
          console.debug('Face shape detection error:', data.errorCode, data.errorDetail);
          showToast('Face Shape Detection', 'Could not detect face shape. Please try again.', 'default');
        } else if (data.faceshape) {
          detectedFaceShape = data.faceshape;
          
          // Show success message
          const article = data.faceshape === 'oval' ? 'an' : 'a';
          showToast(
            'Face Shape Detected', 
            `We detected you have ${article} ${data.faceshape} face shape. Frames are now personalized for you!`,
            'default'
          );
          
          // Update recommended frames UI based on detected face shape
          updateRecommendedFramesUI();
        }
      },
      onIssue: function(issueData) {
        const friendlyError = getUserFriendlyErrorMessage(issueData);
        
        if (friendlyError) {
          showToast(friendlyError.title, friendlyError.message, friendlyError.variant);
        } else {
          console.warn('Unknown VTO issue:', issueData);
        }
      },
    };
    
    try {
      // Create VTO widget - onInitDone is the THIRD parameter
      vtoInstance = window.FitMix.createWidget(
        'fittingbox-vto-container',
        config,
        function() {
          isInitialized = true;
        }
      );
    } catch (error) {
      console.debug('Error creating Fittingbox widget:', error);
      showToast('Initialization Error', 'Could not start virtual try-on. Please reload the page.', 'destructive');
    }
  }

  // ====================================================================
  // VTO CODE SNIPPET Oct 29 2025 - Parse Full Payload Data
  // Option 2: Full Payload - parses current frame + recommended frames
  // ====================================================================
  function parseFullPayloadData() {
    if (!triggerBtn) {
      console.debug('VTO trigger button not found');
      return false;
    }
    
    // Load API key from data attribute (theme settings)
    FITTINGBOX_API_KEY = triggerBtn.getAttribute('data-api-key');
    if (!FITTINGBOX_API_KEY) {
      console.debug('Fittingbox API key not configured in theme settings');
      showToast('Configuration Error', 'Virtual try-on not configured. Please contact support.', 'destructive');
      return false;
    }
    
    // Parse current frame data (with SKU cleaning)
    const rawSku = triggerBtn.getAttribute('data-frame-sku');
    currentFrameData = {
      sku: cleanFrameSKU(rawSku),
      name: triggerBtn.getAttribute('data-frame-name'),
      brand: triggerBtn.getAttribute('data-frame-brand'),
      price: parseFloat(triggerBtn.getAttribute('data-frame-price')),
      imageUrl: triggerBtn.getAttribute('data-frame-image'),
      compatibleFaceShapes: JSON.parse(triggerBtn.getAttribute('data-frame-compatible-shapes') || '[]'),
    };
    
    // Parse recommended frames array (Full Payload)
    try {
      const recommendedFramesJson = triggerBtn.getAttribute('data-recommended-frames');
      recommendedFrames = JSON.parse(recommendedFramesJson || '[]');
    } catch (error) {
      console.debug('Error parsing recommended frames:', error);
      recommendedFrames = [];
    }
    
    // ====================================================================
    // VTO Color Variant add-on - Parse color variants array
    // ====================================================================
    try {
      const colorVariantsJson = triggerBtn.getAttribute('data-color-variants');
      colorVariants = JSON.parse(colorVariantsJson || '[]');
      currentVariantId = parseInt(triggerBtn.getAttribute('data-current-variant-id'));
    } catch (error) {
      console.debug('Error parsing color variants:', error);
      colorVariants = [];
    }
    
    return currentFrameData.sku != null;
  }

  // ====================================================================
  // VTO CODE SNIPPET Oct 29 2025 - Modal Management
  // ====================================================================
  function showModal() {
    if (modal) {
      modal.style.display = 'block';
      document.body.style.overflow = 'hidden';
      
      // Update modal title
      if (modalTitle && currentFrameData) {
        modalTitle.textContent = 'Try On: ' + currentFrameData.brand + ' ' + currentFrameData.name;
      }
      
      // VTO Color Variant add-on - Render color swatches
      renderColorVariantSwatches();
      
      // Show privacy notice
      if (privacyNotice) {
        privacyNotice.style.display = 'flex';
      }
    }
  }

  function hideModal() {
    if (modal) {
      modal.style.display = 'none';
      document.body.style.overflow = '';
      
      // Stop VTO if running
      if (vtoInstance && isVtoRunning) {
        try {
          vtoInstance.stopVto();
          isVtoRunning = false;
          // PD MEASUREMENT - Hide PD button when modal closes
          // hidePDMeasurementButton();
        } catch (e) {
          // Silent cleanup
        }
      }
    }
  }

  // ====================================================================
  // VTO CODE SNIPPET Oct 29 2025 - Start VTO with Frame
  // Uses modern setFrame() API method
  // ====================================================================
  function startVtoWithFrame(frameSku) {
    if (!vtoInstance || !isInitialized) {
      console.debug('VTO not initialized');
      showToast('Not Ready', 'Virtual try-on is still loading. Please wait...', 'default');
      return;
    }
    
    try {
      vtoInstance.setFrame(frameSku);
      vtoInstance.startVto('live');
      isVtoRunning = true;
      vtoHasBeenUsed = true; // Mark VTO as successfully used
      console.log('🎥 VTO started successfully - camera should be active');
      
      // PD MEASUREMENT - Show PD button when camera is active
      // showPDMeasurementButton();
      
      // Ensure button stays visible after VTO starts
      if (triggerBtn) {
        triggerBtn.style.display = '';
        console.log('✓ VTO button visibility ensured after camera start');
      }
      
      // Hide privacy notice
      if (privacyNotice) {
        privacyNotice.style.display = 'none';
      }
      
      // Trigger face shape detection after VTO starts
      setTimeout(function() {
        detectFaceShape();
      }, 2000);
      
    } catch (error) {
      console.debug('Error starting VTO:', error);
      showToast('Error', 'Could not start virtual try-on. Please try again.', 'destructive');
    }
  }

  // ====================================================================
  // VTO CODE SNIPPET Oct 29 2025 - Face Shape Detection
  // ====================================================================
  function detectFaceShape() {
    if (!vtoInstance || !isInitialized) {
      return;
    }
    
    try {
      // Switch to faceshape mode
      vtoInstance.startVto('faceshape');
      
      // Automatically return to live mode after detection
      setTimeout(function() {
        if (vtoInstance && currentFrameData) {
          vtoInstance.startVto('live');
          vtoInstance.setFrame(currentFrameData.sku);
        }
      }, 1500);
      
    } catch (error) {
      console.debug('Error detecting face shape:', error);
    }
  }

  // ====================================================================
  // VTO CODE SNIPPET Oct 29 2025 - Lens Material Selection
  // Uses official Fittingbox Lens Simulator API
  // API: setLensMaterial(sku), setExposureLevel(0-100), restoreLensDefaultMaterial()
  // ====================================================================
  function applyLensMaterial(lensType, lensSku, exposure) {
    if (!vtoInstance || !isInitialized) {
      return;
    }
    
    try {
      if (lensType === 'clear') {
        // Clear/restore default lenses
        vtoInstance.restoreLensDefaultMaterial();
        showToast('Lens Updated', 'Lenses restored to clear', 'default');
      } else if (lensSku) {
        // Apply lens material using Fittingbox SKU
        vtoInstance.setLensMaterial(lensSku);
        
        // For photochromic lenses, set exposure level (0-100)
        if (lensType === 'photochromic' && exposure !== undefined) {
          const exposureLevel = Math.round(exposure * 100); // Convert 0-1 to 0-100
          vtoInstance.setExposureLevel(exposureLevel);
        }
        
        showToast('Lens Updated', 'Lens material applied successfully', 'default');
      } else {
        console.debug('No lens SKU provided for type:', lensType);
        showToast('Configuration Error', 'Lens SKU not configured. Contact Fittingbox for lens material SKUs.', 'default');
      }
    } catch (error) {
      console.debug('Error applying lens:', error);
      showToast('Error', 'Could not apply lens material. Check console for details.', 'default');
    }
  }

  // ====================================================================
  // VTO Color Variant add-on - Color Variant Switching
  // Allows customers to try different frame colors without leaving VTO
  // ====================================================================
  function switchColorVariant(variantId) {
    if (!vtoInstance || !isInitialized) {
      console.debug('VTO not initialized');
      return;
    }
    
    // Find the variant data
    const variant = colorVariants.find(function(v) {
      return v.variantId === variantId;
    });
    
    if (!variant) {
      console.debug('Variant not found:', variantId);
      return;
    }
    
    try {
      // Update current variant ID
      currentVariantId = variantId;
      
      // Switch the frame in VTO
      vtoInstance.setFrame(variant.fittingboxSku);
      
      // Update current frame data
      currentFrameData.sku = variant.fittingboxSku;
      currentFrameData.imageUrl = variant.imageUrl;
      currentFrameData.price = variant.price;
      
      // Update modal title if needed
      if (modalTitle) {
        modalTitle.textContent = 'Try On: ' + currentFrameData.brand + ' ' + currentFrameData.name + ' - ' + variant.colorName;
      }
      
      // Re-render color swatches to update active state
      renderColorVariantSwatches();
    } catch (error) {
      console.debug('Error switching color variant:', error);
      showToast('Error', 'Could not switch frame color', 'default');
    }
  }
  
  function renderColorVariantSwatches() {
    const colorSwatchContainer = document.getElementById('vto-color-swatches');
    
    if (!colorSwatchContainer || colorVariants.length <= 1) {
      // Hide container if only one variant (no choice to make)
      if (colorSwatchContainer) {
        colorSwatchContainer.parentElement.style.display = 'none';
      }
      return;
    }
    
    // VTO CODE SNIPPET Oct 31 2025 - Check if all variants share the same SKU
    // If they do, hide color swatches to avoid confusion (variant-level metafields not configured)
    const uniqueSkus = new Set(colorVariants.map(function(v) { return v.fittingboxSku; }));
    if (uniqueSkus.size === 1) {
      console.warn('⚠ All color variants share the same Fittingbox SKU:', Array.from(uniqueSkus)[0]);
      console.warn('⚠ Color switching disabled. Please configure variant-level metafields for each color.');
      console.warn('⚠ See SHOPIFY_INTEGRATION_GUIDE.md Step 2b for setup instructions.');
      if (colorSwatchContainer) {
        colorSwatchContainer.parentElement.style.display = 'none';
      }
      return;
    }
    
    // Show container
    colorSwatchContainer.parentElement.style.display = 'block';
    
    // Clear existing swatches
    colorSwatchContainer.innerHTML = '';
    
    // Create swatch for each color variant
    colorVariants.forEach(function(variant) {
      const swatch = document.createElement('button');
      swatch.className = 'vto-color-swatch';
      swatch.setAttribute('data-variant-id', variant.variantId);
      swatch.setAttribute('title', variant.colorName);
      
      // Add active class if this is the current variant
      if (variant.variantId === currentVariantId) {
        swatch.classList.add('active');
      }
      
      // Add unavailable class if out of stock
      if (!variant.available) {
        swatch.classList.add('unavailable');
        swatch.disabled = true;
      }
      
      // Use variant image as thumbnail
      swatch.innerHTML = `
        <img src="${escapeHTML(variant.imageUrl)}" alt="${escapeHTML(variant.colorName)}" class="vto-swatch-image" />
        <span class="vto-swatch-label">${escapeHTML(variant.colorName)}</span>
      `;
      
      // Click handler
      if (variant.available) {
        swatch.addEventListener('click', function() {
          switchColorVariant(variant.variantId);
        });
      }
      
      colorSwatchContainer.appendChild(swatch);
    });
  }

  // ====================================================================
  // VTO CODE SNIPPET Oct 29 2025 - Recommended Frames UI
  // Filters based on detected face shape
  // ====================================================================
  function updateRecommendedFramesUI() {
    const recommendedContainer = document.getElementById('vto-recommended-container');
    if (!recommendedContainer || recommendedFrames.length === 0) {
      // Hide recommended section if no frames
      const recommendedSection = document.getElementById('vto-recommended-section');
      if (recommendedSection) {
        recommendedSection.style.display = 'none';
      }
      return;
    }
    
    // Show recommended section
    const recommendedSection = document.getElementById('vto-recommended-section');
    if (recommendedSection) {
      recommendedSection.style.display = 'block';
    }
    
    // Filter frames based on detected face shape
    let filteredFrames = recommendedFrames;
    
    if (detectedFaceShape) {
      const compatibleFrames = recommendedFrames.filter(function(frame) {
        return frame.compatibleFaceShapes && 
               frame.compatibleFaceShapes.includes(detectedFaceShape);
      });
      
      // Use compatible frames if any exist, otherwise show all
      if (compatibleFrames.length > 0) {
        filteredFrames = compatibleFrames;
      }
    }
    
    // Clear existing content
    recommendedContainer.innerHTML = '';
    
    // Render frames
    filteredFrames.forEach(function(frame) {
      const frameCard = document.createElement('div');
      frameCard.className = 'vto-frame-card';
      
      const isCompatible = detectedFaceShape && 
                          frame.compatibleFaceShapes && 
                          frame.compatibleFaceShapes.includes(detectedFaceShape);
      
      frameCard.innerHTML = `
        ${isCompatible ? '<div class="vto-perfect-match-badge">Perfect Match</div>' : ''}
        <img src="${escapeHTML(frame.imageUrl)}" alt="${escapeHTML(frame.name)}" class="vto-frame-image" />
        <div class="vto-frame-info">
          <div class="vto-frame-brand">${escapeHTML(frame.brand)}</div>
          <div class="vto-frame-name">${escapeHTML(frame.name)}</div>
          <div class="vto-frame-price">$${frame.price.toFixed(2)}</div>
        </div>
        <button class="vto-try-frame-btn" data-frame-sku="${escapeHTML(frame.sku)}">Try On</button>
      `;
      
      recommendedContainer.appendChild(frameCard);
    });
    
    // Attach event listeners
    attachRecommendedFrameListeners();
  }

  function attachRecommendedFrameListeners() {
    document.querySelectorAll('.vto-try-frame-btn').forEach(function(btn) {
      btn.addEventListener('click', function() {
        const frameSku = this.getAttribute('data-frame-sku');
        
        // Find frame data
        const frameData = recommendedFrames.find(function(f) {
          return f.sku === frameSku;
        });
        
        if (frameData) {
          // Update current frame data
          currentFrameData = frameData;
          
          // Update modal title
          if (modalTitle) {
            modalTitle.textContent = 'Try On: ' + frameData.brand + ' ' + frameData.name;
          }
          
          // Load the frame
          startVtoWithFrame(frameSku);
        }
      });
    });
  }

  // ====================================================================
  // VTO CODE SNIPPET Oct 29 2025 - Event Listeners
  // ====================================================================
  
  // Open VTO Modal
  if (triggerBtn) {
    triggerBtn.addEventListener('click', function() {
      // Parse data from button
      if (!parseFullPayloadData()) {
        showToast('Error', 'Frame data not available', 'destructive');
        return;
      }
      
      showModal();
      
      // Initialize Fittingbox on first open
      if (!vtoInstance) {
        waitForFittingbox(initializeFittingbox);
      }
      
      // Update UI
      updateRecommendedFramesUI();
      // updateTop3UI();
    });
  }

  // Close Modal
  if (closeBtn) {
    closeBtn.addEventListener('click', function() {
      hideModal();
    });
  }

  // Privacy: Accept
  if (acceptPrivacyBtn) {
    acceptPrivacyBtn.addEventListener('click', function() {
      if (!currentFrameData || !currentFrameData.sku) {
        showToast('Error', 'No frame selected', 'default');
        return;
      }
      
      // Check if VTO is already initialized
      if (isInitialized) {
        startVtoWithFrame(currentFrameData.sku);
      } else {
        // Show initializing message
        showToast('Initializing...', 'Getting virtual try-on ready for you', 'default');
        
        // Wait for initialization, then start VTO
        waitForInitialization(function() {
          startVtoWithFrame(currentFrameData.sku);
        });
      }
    });
  }

  // Privacy: Decline
  if (declinePrivacyBtn) {
    declinePrivacyBtn.addEventListener('click', function() {
      hideModal();
    });
  }

  // Close modal on overlay click
  if (modal) {
    modal.addEventListener('click', function(e) {
      if (e.target === modal) {
        hideModal();
      }
    });
  }

  // ====================================================================
  // VTO CODE SNIPPET Oct 29 2025 - Lens Selector Event Listeners
  // Wire up lens material selection buttons
  // Maps button labels to placeholder constants (update constants at top of file)
  // ====================================================================
  const lensTypeButtons = document.querySelectorAll('[data-lens-type]');
  lensTypeButtons.forEach(function(btn) {
    btn.addEventListener('click', function() {
      const lensType = this.getAttribute('data-lens-type');
      const exposure = parseFloat(this.getAttribute('data-lens-exposure')) || 0.5;
      const buttonText = this.textContent.trim().toLowerCase();
      
      // Map button text to placeholder constants
      let lensSku = null;
      if (buttonText.includes('tint')) {
        lensSku = LENS_SKU_TINTED;
      } else if (buttonText.includes('mirror')) {
        lensSku = LENS_SKU_MIRROR;
      } else if (buttonText.includes('photochromic')) {
        lensSku = LENS_SKU_PHOTOCHROMIC;
      }
      
      applyLensMaterial(lensType, lensSku, exposure);
      
      // Update active state
      lensTypeButtons.forEach(function(b) { b.classList.remove('active'); });
      this.classList.add('active');
    });
  });

  // ====================================================================
  // VTO CODE SNIPPET Oct 29 2025 - Frame Validation on Page Load
  // Validates frame SKU and shows/hides VTO button accordingly
  // ====================================================================
  if (triggerBtn) {
    // Get API key and frame SKU from button
    const apiKey = triggerBtn.getAttribute('data-api-key');
    const frameSku = triggerBtn.getAttribute('data-frame-sku');
    
    if (apiKey && frameSku) {
      // Validate frame availability
      triggerBtn.style.display = '';
      // validateFrameAvailability(frameSku, apiKey, function(isAvailable) {
      //   if (isAvailable) {
      //     // Frame is available - show the button
      //     console.log('✅ VTO button shown - frame is available');
      //     triggerBtn.style.display = '';
      //   } else {
      //     // Frame not available - only hide if validation hasn't already passed
      //     // This prevents the button from disappearing after VTO starts
      //     if (!validationPassed && !vtoHasBeenUsed && !isVtoRunning) {
      //       console.log('❌ VTO button hidden - frame not available');
      //       triggerBtn.style.display = 'none';
      //     } else {
      //       console.log('⚠ Frame validation failed, but keeping button visible (VTO in use)');
      //     }
      //   }
      // });
    } else {
      // Missing API key or SKU - hide button only if VTO hasn't been used
      if (triggerBtn && !validationPassed && !vtoHasBeenUsed) {
        triggerBtn.style.display = 'none';
      }
      if (!apiKey) console.warn('⚠ VTO API key not configured');
      if (!frameSku) console.warn('⚠ Frame SKU not found on product');
    }
  }

})();
